import type { CardDetail, CardPricePoint } from "@hobbyshopify/backend-client";
import { type ReactNode, type Ref, useEffect, useId, useRef, useState } from "react";
import { useObserver, useSubtree } from "subtree.js/react";
import { BottomBar, useSaidInView } from "../../components/BottomBar";
import { CardPhotos, PAGE_WIDE, PHOTOS_BESIDE, PHOTOS_SPAN } from "../../components/CardPhotos";
import { EbayGradeLinks } from "../../components/EbayGradeLinks";
import { Hint, MoreDetailsToggle, SectionTitle, TextField } from "../../components/EditorPieces";
import { GradeCombobox } from "../../components/GradeCombobox";
import { gridGradeKey } from "../../components/grade-grid";
import { LoadFailed, Loading } from "../../components/LoadState";
import { MarketEstimatePanel } from "../../components/MarketEstimatePanel";
import { ConfirmDialog } from "../../components/ModalLayer";
import { PricePanel } from "../../components/PricePanel";
import { SheetBack } from "../../components/SheetBack";
import { useFocusFallback } from "../../components/use-focus-fallback";
import { cardTitle, formatCents, gradeLabel } from "../../core/primitives/card-display";
import {
  type CardSection,
  cardIdentityEdit,
  cardYearBrandNumber,
  changesIdentity,
  floorError,
  gradeError,
  isDirty,
  isSaveable,
  playerError,
  priceError,
  priceKindLabel,
  priceOutOfSync,
  recentPrices,
  reservedOf,
  saveTitle,
  saysUnread,
  stockByLocation,
  stockHint,
  yearError,
} from "../../core/primitives/card-edit";
import { idleMarketRefresh } from "../../core/primitives/catalog-market";
import { formatDate, formatWhen } from "../../core/primitives/format";
import { SEALED_CATEGORY_LABELS, SEALED_PRODUCT_TYPE_LABELS, sealedGuides } from "../../core/primitives/sealed-edit";
import { CardDetailActions, CardDetailState, cardEditing, cardSaveBar } from "./card-detail-model";

/** Label and value lines in one box, as the card's location counts and price history read. */
function Rows({ rows, accent = false }: { rows: { key: string; label: string; value: string }[]; accent?: boolean }) {
  return (
    <ul className="divide-y divide-line rounded-row border border-line bg-surface px-3.5">
      {rows.map((row) => (
        <li key={row.key} className="flex items-center justify-between gap-2 py-2.5">
          <span className="min-w-0 truncate text-[13px] text-muted">{row.label}</span>
          <span className={`shrink-0 text-sm font-semibold tabular-nums ${accent ? "text-accent" : "text-white"}`}>
            {row.value}
          </span>
        </li>
      ))}
    </ul>
  );
}

const SOURCE_LABELS: Record<string, string> = { scp: "SportsCardsPro", price_charting: "PriceCharting" };

/** The sections a Stock row's menu opens the card at. */
const SECTION_IDS: Record<CardSection, string> = { price: "card-price-and-stock", history: "card-price-history" };

const WAITING_NOTE = "A change to this card is still on its way. The page updates once it lands; Save waits for it.";
const WAITING_NOTE_VIEW = "A change to this card is still on its way. The page updates once it lands.";
const LANDED = "The change on its way has landed.";
const LANDED_UNREAD = "The change on its way has landed, but the card could not be read again.";

function Section({ title, id, children }: { title: string; id?: string; children: ReactNode }) {
  return (
    <section id={id} className="scroll-mt-12 space-y-2.5">
      <SectionTitle>{title}</SectionTitle>
      {children}
    </section>
  );
}

/**
 * One card, on one page, edited in place: a port of the iOS `AdminCardScreen`,
 * section by section. Changes wait for the Save bar at the bottom.
 */
export function CardDetailPage() {
  const state = useSubtree(CardDetailState);
  const actions = useSubtree(CardDetailActions);
  const observer = useObserver();
  const [showMore, setShowMore] = useState(false);
  const gradeId = useId();
  const notesRef = useRef<HTMLDivElement>(null);

  const detail = observer.watch(state.detail);
  const status = observer.watch(state.status);
  const fields = observer.watch(state.fields);
  const history = observer.watch(state.history);
  const lossKind = observer.watch(state.lossKind);
  const savingFrom = observer.watch(state.savingFrom);
  const lossNote = observer.watch(state.lossNote);
  const saving = observer.watch(state.saving);
  const repricing = observer.watch(state.repricing);
  const archiving = observer.watch(state.archiving);
  const confirmingArchive = observer.watch(state.confirmingArchive);
  const error = observer.watch(state.error);
  const archiveError = observer.watch(state.archiveError);
  const notice = observer.watch(state.notice);
  const waiting = observer.watch(state.waiting);
  const landedUnread = observer.watch(state.landedUnread);
  const quoteNote = observer.watch(state.quoteNote);
  const canEdit = observer.watch(state.canEditInventory);
  const scrollTo = observer.watch(state.scrollTo);

  // While the Save bar is up, what an action answers shows in it, where the merchant is looking. It is up too while a
  // change another page made is on its way, to say so wherever the page is, and it goes without moving the page.
  const dirty = detail !== null && fields !== null && isDirty(cardEditing(detail, fields, lossKind, lossNote));
  const barUp = detail !== null && fields !== null && ((canEdit && dirty) || waiting || landedUnread);
  useSaidInView(notesRef, error, !barUp, saving || repricing || archiving);
  // A control in the bar going with the focus in it (saved, put back, the card turned out as typed, the bar staying
  // for a change on its way): the focus goes to the title.
  const titleRef = useRef<HTMLHeadingElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  useFocusFallback(barRef, titleRef);
  // The wait and its landing are said in a region already on the page once the card shows, so both are announced.
  const shown = detail !== null && fields !== null;
  const wasWaiting = useRef(false);
  const wasUnread = useRef(false);
  const [waitSaid, setWaitSaid] = useState("");
  const waitingNote = canEdit ? WAITING_NOTE : WAITING_NOTE_VIEW;
  useEffect(() => {
    if (!shown) {
      setWaitSaid("");
      wasWaiting.current = false;
      wasUnread.current = false;
      return;
    }
    if (waiting) setWaitSaid(waitingNote);
    // A read after the landing that failed is said by the bar, as an alert; the read that answers after it, here.
    else if (wasWaiting.current || (wasUnread.current && !landedUnread)) setWaitSaid(landedUnread ? "" : LANDED);
    wasWaiting.current = waiting;
    wasUnread.current = landedUnread;
  }, [waiting, shown, landedUnread, waitingNote]);

  // Opened from a Stock row's menu: the section comes to the top once the card and its prices are read, as on iOS.
  useEffect(() => {
    if (!scrollTo) return;
    const still = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    document
      .getElementById(SECTION_IDS[scrollTo])
      ?.scrollIntoView({ behavior: still ? "auto" : "smooth", block: "start" });
    actions.scrolled();
  }, [scrollTo, actions]);

  if (!detail || !fields) {
    return (
      <div className={`mx-auto max-w-[700px] px-4 py-4 sm:px-5 ${PAGE_WIDE}`}>
        <SheetBack onBack={actions.back} />
        {status.kind === "error" ? (
          <LoadFailed title="Couldn't load this card." message={status.message} onRetry={actions.retry} />
        ) : (
          <Loading label="Loading the card…" />
        )}
      </div>
    );
  }

  const { card, sealed } = detail;
  // A sealed box's `card` is a card-shaped stand-in; its own words are on `item`.
  const title = sealed ? (detail.item?.title ?? cardTitle(card)) : cardTitle(card);
  const editing = cardEditing(detail, fields, lossKind, lossNote);
  const busy = saving || repricing || archiving;
  const canSave = canEdit && !busy && isSaveable(editing);
  const reserved = reservedOf(detail);
  const saveBar = cardSaveBar(editing, savingFrom);
  const identityChanging = changesIdentity(cardIdentityEdit(fields, card, editing.base), card);
  const subtitle = sealed ? (detail.item?.subtitle ?? "") : cardYearBrandNumber(card);
  const hint = stockHint(card, reserved);
  const grade = gradeLabel(card);
  const readOnly = !canEdit || saving;
  const nameError = playerError(fields, editing.base);
  // A field with an error stays in view, or Save would be off with nothing saying why.
  const moreOpen = showMore || nameError !== null;

  return (
    // Room for the Save bar whether it is up or not, so the page's end does not move when the bar comes or goes.
    <div className={`mx-auto max-w-[700px] px-4 py-4 pb-36 sm:px-5 ${PAGE_WIDE} ${PHOTOS_BESIDE}`}>
      <header className={`mb-4 flex items-center gap-1 ${PHOTOS_SPAN}`}>
        <SheetBack onBack={actions.back} />
        <h1
          ref={titleRef}
          tabIndex={-1}
          className="min-w-0 flex-1 truncate text-center text-[17px] font-semibold text-white"
        >
          {title}
        </h1>
        <span className="w-11 shrink-0" />
      </header>
      <p role="status" className="sr-only">
        {waitSaid}
      </p>
      {/* On the page before what it says, so a saved change is heard while the focus moves to the title. */}
      <p role="status" className="sr-only">
        {notice ?? ""}
      </p>

      <div className="mb-6 space-y-2.5 lg:mb-0">
        <CardPhotos
          shown={[
            { url: card.imageUrl, caption: "Front", missing: "No front" },
            { url: card.backImageUrl, caption: "Back", missing: "No back" },
          ]}
          gallery={[
            { url: card.imageUrl, caption: "Front" },
            { url: card.backImageUrl, caption: "Back" },
          ]}
        />
        {subtitle && <p className="text-xs text-muted">{subtitle}</p>}
      </div>

      <div className="space-y-6">
        {quoteNote && <p className="note note-neutral text-muted">{quoteNote}</p>}

        <Notes
          ref={notesRef}
          detail={detail}
          error={barUp ? null : error}
          notice={notice}
          reloadFailed={status.kind === "error" && !landedUnread && !saysUnread(error) ? status.message : null}
        />

        <Section title="Price and stock" id={SECTION_IDS.price}>
          <PricePanel
            price={fields.price}
            onPrice={(price) => actions.setFields({ price })}
            count={{
              value: fields.onHand,
              onChange: (onHand) => actions.setFields({ onHand }),
              label: card.trackStock ? "On hand" : "Not counted",
              minimum: reserved,
            }}
            separateFloor={fields.separateFloor}
            onSeparateFloor={actions.setSeparateFloor}
            floor={fields.floor}
            onFloor={(floor) => actions.setFields({ floor })}
            floorError={floorError(fields)}
            priceError={priceError(fields)}
            disabled={readOnly}
          >
            {saveBar.asksWhy ? (
              <div className="space-y-2.5 border-t border-line pt-3">
                <p className="text-xs font-medium text-muted">Why fewer?</p>
                <div className="flex rounded-btn bg-surface-strong p-1 text-sm font-medium">
                  {(
                    [
                      ["SHRINKAGE", "Lost or damaged"],
                      ["ADJUSTED", "Correction"],
                    ] as const
                  ).map(([kind, label]) => (
                    <button
                      key={kind}
                      type="button"
                      aria-pressed={lossKind === kind}
                      onClick={() => actions.setLossKind(kind)}
                      disabled={readOnly}
                      className={`flex-1 rounded-md px-2 py-1.5 transition-colors ${
                        lossKind === kind ? "bg-app text-white" : "text-muted hover:text-white"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <TextField
                  label="Note"
                  value={lossNote}
                  onChange={actions.setLossNote}
                  placeholder="Optional"
                  readOnly={readOnly}
                />
                <Hint>Steps on the Stock list save as corrections.</Hint>
              </div>
            ) : (
              hint && <Hint>{hint}</Hint>
            )}
          </PricePanel>
        </Section>

        {sealed ? (
          <Section title="Product">
            <Rows
              rows={[
                ["Category", SEALED_CATEGORY_LABELS[sealed.category] ?? sealed.category],
                ["Type", SEALED_PRODUCT_TYPE_LABELS[sealed.productType] ?? sealed.productType],
                ["Set", sealed.setName],
                ["Variant", sealed.productVariant],
                ["Brand", card.brand],
                ["Year", card.year ? String(card.year) : null],
                ["Configuration", sealed.configuration],
                ["Language", sealed.language],
                ["Barcode", sealed.upc],
                ["Confirmed", card.confirmedAt ? formatDate(card.confirmedAt) : "Not yet reviewed"],
              ].flatMap(([label, value]) => (value ? [{ key: label as string, label: label as string, value }] : []))}
            />
            {detail.product && (
              <Hint>
                Linked to <span className="text-white">{detail.product.title ?? detail.product.productId}</span> ·{" "}
                {SOURCE_LABELS[detail.product.source] ?? detail.product.source}
              </Hint>
            )}
            {detail.scans.some((scan) => scan.matchKind === "LINKED") && <Hint>Linked by hand from a scan.</Hint>}
          </Section>
        ) : (
          <Section title="Card details">
            <div className="space-y-3">
              <TextField
                label="Parallel"
                value={fields.parallel}
                onChange={(parallel) => actions.setFields({ parallel })}
                placeholder="Base"
                readOnly={readOnly}
              />
              <div className="flex gap-3">
                <TextField
                  label="Year"
                  value={fields.year}
                  onChange={(year) => actions.setFields({ year })}
                  placeholder="2021"
                  inputMode="numeric"
                  error={yearError(fields)}
                  readOnly={readOnly}
                />
                <TextField
                  label="Card #"
                  value={fields.cardNumber}
                  onChange={(cardNumber) => actions.setFields({ cardNumber })}
                  placeholder="42"
                  readOnly={readOnly}
                />
              </div>
              <div>
                <label htmlFor={gradeId} className="mb-1 block text-xs font-medium text-muted">
                  Grade
                </label>
                <GradeCombobox
                  id={gradeId}
                  value={fields.grade}
                  onType={(grade) => actions.setFields({ grade })}
                  onPick={(picked) => actions.setFields({ grade: picked.key === "raw" ? "Raw" : picked.label })}
                  scpData={card.scpData}
                  placeholder="PSA 10, 9.5, raw…"
                  disabled={readOnly}
                />
                {gradeError(fields, editing.base) && (
                  <p className="mt-1 text-xs text-warn">{gradeError(fields, editing.base)}</p>
                )}
              </div>
              <MoreDetailsToggle
                open={moreOpen}
                onToggle={() => setShowMore((open) => !open)}
                disabled={nameError !== null}
              />
              {moreOpen && (
                <>
                  <TextField
                    label="Player or name"
                    value={fields.player}
                    onChange={(player) => actions.setFields({ player })}
                    placeholder="Ja Morant"
                    error={nameError}
                    readOnly={readOnly}
                  />
                  <TextField
                    label="Set"
                    value={fields.setName}
                    onChange={(setName) => actions.setFields({ setName })}
                    placeholder="Prizm"
                    readOnly={readOnly}
                  />
                </>
              )}
              {identityChanging && (
                <Hint warn>Changing what the card is clears its eBay comps. Save looks the price up again.</Hint>
              )}
            </div>
          </Section>
        )}

        {sealed ? (
          <SealedMarket detail={detail} />
        ) : (
          <EbayGradeLinks
            searchLinks={card.searchLinks ?? undefined}
            scpData={card.scpData}
            cardTitle={title}
            cardGradeKey={gridGradeKey(grade === "Raw" ? "" : grade, card.slabbed)}
            onRefresh={canEdit ? () => void actions.refreshPrice() : undefined}
            refreshing={repricing}
            held={saving || archiving}
          />
        )}

        {card.trackStock && detail.lots.length > 0 && (
          <Section title="Stock by location">
            <Rows
              rows={stockByLocation(detail).map((row) => ({
                key: row.name,
                label: row.name,
                value: String(row.quantity),
              }))}
            />
            {detail.lots.some((lot) => lot.unitCostCents != null) && (
              <Rows
                rows={detail.lots.flatMap((lot) =>
                  lot.unitCostCents == null
                    ? []
                    : [
                        {
                          key: lot.id,
                          label: `${lot.quantity} × ${lot.locationName ?? "No location"} · cost`,
                          value: `${formatCents(lot.unitCostCents)} each`,
                        },
                      ],
                )}
              />
            )}
          </Section>
        )}

        <PriceHistory points={history} />

        <Section title="Listed on">
          <div className="flex items-center gap-2.5 rounded-row border border-line bg-surface px-3.5 py-3">
            <span className={`min-w-0 flex-1 truncate text-sm ${detail.channel ? "text-white" : "text-muted"}`}>
              {detail.channel ? capitalized(detail.channel) : "Not listed on any channel"}
            </span>
            {detail.channel && (
              <>
                {detail.listingPriceCents != null && (
                  <span className="text-[13px] tabular-nums text-muted">{formatCents(detail.listingPriceCents)}</span>
                )}
                <span className="badge badge-accent">Active</span>
              </>
            )}
          </div>
        </Section>

        {canEdit && (
          <div className="mt-2 space-y-1.5">
            <button
              type="button"
              onClick={actions.askArchive}
              // Held, not disabled, while it waits: the keys still reach it, and it says why.
              aria-disabled={busy || waiting || undefined}
              aria-describedby={archiveError ? "card-archive-error" : waiting ? "card-waiting" : undefined}
              className="h-[46px] w-full rounded-btn border border-danger/35 text-[15px] font-semibold text-danger aria-disabled:opacity-50"
            >
              {`Archive ${sealed ? "product" : "card"}`}
            </button>
            {archiveError && (
              <p id="card-archive-error" role="alert" className="field-error text-xs">
                {archiveError}
              </p>
            )}
          </div>
        )}
      </div>

      {barUp && (
        <BottomBar>
          <div ref={barRef}>
            {error && (
              <p role="alert" className="mb-1.5 text-xs text-danger">
                {error}
              </p>
            )}
            {waiting && (
              <p id="card-waiting" className="mb-1.5 text-xs text-muted">
                {waitingNote}
              </p>
            )}
            {landedUnread && !waiting && (
              <div className="mb-1.5 flex items-center justify-between gap-3">
                {/* Away while the card is read, so a read that fails again is said again. */}
                {/* Keyed apart, so each failure comes as an alert put on the page, which is what is spoken. */}
                {/* One failure, said once: the Save's sentence above already says the card could not be read. */}
                {status.kind === "loading" ? (
                  <p key="reading" className="text-xs text-muted">
                    Reading the card again…
                  </p>
                ) : saysUnread(error) ? (
                  <span />
                ) : (
                  <p key="unread" role="alert" className="text-xs text-danger">
                    {LANDED_UNREAD}
                  </p>
                )}
                {/* Held, not disabled, while the card is read or a change goes, which reads it after: the focus stays on it. */}
                <button
                  type="button"
                  aria-disabled={status.kind === "loading" || busy || undefined}
                  onClick={status.kind === "loading" || busy ? undefined : actions.retry}
                  className="btn-secondary btn-sm shrink-0 aria-disabled:opacity-40"
                >
                  Read again
                </button>
              </div>
            )}
            {canEdit && dirty && (
              <>
                <p className="mb-2 line-clamp-2 text-xs text-muted">{saveBar.line}</p>
                {/* Held, not disabled: a Save the server refuses leaves the focus where it was pressed. */}
                <div className="flex gap-2.5">
                  <button
                    type="button"
                    onClick={saving ? undefined : actions.discard}
                    aria-disabled={saving || undefined}
                    className="btn-secondary h-12 px-5 aria-disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={canSave ? () => void actions.save() : undefined}
                    aria-disabled={!canSave || undefined}
                    className="btn-primary h-12 flex-1 aria-disabled:opacity-50"
                  >
                    {saving ? "Saving…" : saveTitle(editing)}
                  </button>
                </div>
              </>
            )}
          </div>
        </BottomBar>
      )}

      {confirmingArchive && (
        <ConfirmDialog
          title={`Archive ${title}?`}
          message="It leaves stock and its listing is unpublished. Sales history stays."
          confirmLabel="Archive"
          busyLabel="Archiving…"
          cancelLabel="Keep"
          busy={archiving}
          onConfirm={() => void actions.archive()}
          onCancel={actions.cancelArchive}
        />
      )}
    </div>
  );
}

function Notes({
  ref,
  detail,
  error,
  notice,
  reloadFailed,
}: {
  ref: Ref<HTMLDivElement>;
  detail: CardDetail;
  error: string | null;
  notice: string | null;
  reloadFailed: string | null;
}) {
  const theirs = detail.providerPriceCents;
  const outOfSync = priceOutOfSync(detail) && theirs != null;
  if (!reloadFailed && !error && !notice && !outOfSync) return null;
  return (
    <div ref={ref} className="scroll-mt-12 space-y-2.5">
      {reloadFailed && (
        <p role="alert" className="note note-danger text-danger">
          {reloadFailed}
        </p>
      )}
      {error && (
        <p role="alert" className="note note-danger text-danger">
          {error}
        </p>
      )}
      {notice && <p className="note note-accent text-accent">{notice}</p>}
      {outOfSync && (
        <p className="note note-warn text-warn">Listed at {formatCents(theirs)} online. Saving a price updates it.</p>
      )}
    </div>
  );
}

function PriceHistory({ points }: { points: readonly CardPricePoint[] }) {
  if (points.length === 0) return null;
  return (
    <Section title="Price history" id={SECTION_IDS.history}>
      <Rows
        accent
        rows={recentPrices(points).map((point, i) => ({
          key: `${point.recordedAt.toISOString()}-${i}`,
          label: `${formatWhen(point.recordedAt)} · ${priceKindLabel(point.kind)}`,
          value: formatCents(point.priceCents),
        }))}
      />
    </Section>
  );
}

/** A sealed box's market: the shared estimate, else the price guide's numbers, and its sold listings. */
function SealedMarket({ detail }: { detail: CardDetail }) {
  const { card } = detail;
  const guides = sealedGuides(card.scpData);
  if (!detail.marketEstimate && guides.marketCents == null && !card.searchLinks?.sealed) return null;
  return (
    <Section title="Market">
      {detail.marketEstimate ? (
        <MarketEstimatePanel estimate={detail.marketEstimate} refresh={idleMarketRefresh} />
      ) : (
        guides.marketCents != null && (
          <p className="text-xs text-muted">
            Market {formatCents(guides.marketCents)}
            {guides.dealerBuyCents != null && ` · dealers pay ${formatCents(guides.dealerBuyCents)}`}
            {guides.dealerSellCents != null && ` · dealers ask ${formatCents(guides.dealerSellCents)}`}
          </p>
        )
      )}
      {card.searchLinks?.sealed && (
        <a href={card.searchLinks.sealed} target="_blank" rel="noreferrer" className="btn-secondary w-full">
          Sold listings on eBay
        </a>
      )}
    </Section>
  );
}

const capitalized = (text: string) => text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
